import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Package, Search, Clock, CheckCircle2, RefreshCw, Truck, PackageCheck, XCircle,
  MapPin, Receipt, Copy, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'pending',    label: 'Order Placed',     icon: Clock },
  { key: 'accepted',   label: 'Confirmed',        icon: CheckCircle2 },
  { key: 'processing', label: 'Being Packed',     icon: RefreshCw },
  { key: 'shipped',    label: 'Shipped',          icon: Truck },
  { key: 'delivered',  label: 'Delivered',        icon: PackageCheck },
];

const STATUS_COPY = {
  pending:    'Your order has been received and payment confirmed. We will start preparing it shortly.',
  accepted:   'We have confirmed your order and it is queued for packing.',
  processing: 'Your fragrances are being hand-packed at our Kannauj workshop.',
  shipped:    'Your order has been handed to the courier. Track with the link below.',
  delivered:  'Your parcel has been delivered. We hope you love it — and thank you from Kannauj.',
  cancelled:  'This order was cancelled. If you had paid, a refund has been initiated.',
  refunded:   'A refund has been issued for this order and should reach your bank within 5–7 working days.',
};

const fmtINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export const TrackOrder = () => {
  const [params, setParams] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get('order_id') || '');
  const [email, setEmail] = useState(params.get('email') || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOrder = async (oid = orderId, em = email) => {
    if (!oid || !em) {
      setError('Both Order ID and Email are required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/orders/track`, { params: { order_id: oid.trim(), email: em.trim() } });
      setOrder(data);
      setParams({ order_id: oid.trim(), email: em.trim() }, { replace: true });
    } catch (err) {
      setOrder(null);
      setError(err?.response?.data?.detail || 'Unable to find this order.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-load if query params present
  useEffect(() => {
    if (params.get('order_id') && params.get('email')) {
      fetchOrder(params.get('order_id'), params.get('email'));
    }
    // eslint-disable-next-line
  }, []);

  const handleSubmit = (e) => { e.preventDefault(); fetchOrder(); };

  const copy = (t, l) => { navigator.clipboard.writeText(t); toast.success(`${l} copied`); };

  const currentStep = order ? STATUS_STEPS.findIndex(s => s.key === order.order_status) : -1;
  const isTerminalCancelled = order && ['cancelled', 'refunded'].includes(order.order_status);

  return (
    <div data-testid="track-order-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="mb-12">
            <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground flex items-center gap-4 mb-3">
              <Package className="text-primary" size={48} strokeWidth={1.5} />
              <span>Track Your <span className="italic">Order</span></span>
            </h1>
            <p className="text-muted-foreground">
              Enter your Order ID and the email you used at checkout. You'll see live status, shipping details and tracking link.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="border border-primary/20 p-6 md:p-8 mb-10 space-y-5 bg-background/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-widest font-medium block mb-2 text-muted-foreground">Order ID *</label>
                <input
                  type="text"
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  required
                  data-testid="track-order-id"
                  placeholder="e.g. cd09d64f-11ff-..."
                  className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-base font-mono tracking-wider"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-medium block mb-2 text-muted-foreground">Email Used at Checkout *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="track-order-email"
                  placeholder="you@example.com"
                  className="w-full bg-transparent border-b border-white/20 py-3 focus:border-primary focus:outline-none text-base"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="track-order-submit"
              className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Search size={16} strokeWidth={1.8} />
              <span>{loading ? 'Searching...' : 'Track Order'}</span>
            </button>
            {error && <p className="text-sm text-red-400" data-testid="track-order-error">{error}</p>}
            <p className="text-xs text-muted-foreground">
              Can't find your Order ID? It was shown on-screen after payment and emailed informally via your order confirmation.
              Need help? <a href="/contact" className="text-primary hover:underline">Contact us</a>.
            </p>
          </form>

          {order && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-8"
              data-testid="track-order-result"
            >
              {/* Status headline */}
              <div className="border border-primary/20 p-6 md:p-8 bg-gradient-to-br from-primary/5 to-transparent">
                <div className="flex items-start justify-between flex-wrap gap-4 mb-2">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Order</p>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="font-mono text-lg">#{order.id.slice(0, 13)}...</code>
                      <button onClick={() => copy(order.id, 'Order ID')} className="text-muted-foreground hover:text-primary">
                        <Copy size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Placed on {fmtDate(order.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Paid</p>
                    <p className="font-accent text-3xl italic text-primary">{fmtINR(order.total_amount)}</p>
                    <p className="text-xs text-muted-foreground mt-1">(All taxes incl.)</p>
                  </div>
                </div>
                <p className="mt-4 text-base">{STATUS_COPY[order.order_status] || ''}</p>
              </div>

              {/* Stepper */}
              {!isTerminalCancelled ? (
                <div className="border border-white/10 p-6 md:p-8">
                  <h3 className="font-display text-2xl mb-6">Progress</h3>
                  <div className="relative">
                    <div className="hidden md:block absolute top-5 left-8 right-8 h-0.5 bg-white/10" />
                    <div
                      className="hidden md:block absolute top-5 left-8 h-0.5 bg-primary transition-all duration-700"
                      style={{ width: currentStep >= 0 ? `${(currentStep / (STATUS_STEPS.length - 1)) * (100 - 8 * 100 / (STATUS_STEPS.length - 1) / 4)}%` : '0%', maxWidth: 'calc(100% - 4rem)' }}
                    />
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-0 relative">
                      {STATUS_STEPS.map((step, idx) => {
                        const Icon = step.icon;
                        const reached = idx <= currentStep;
                        const active = idx === currentStep;
                        return (
                          <div key={step.key} className="flex md:flex-col items-center md:items-start gap-3 md:gap-2">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                              reached ? 'bg-primary border-primary text-primary-foreground' : 'border-white/20 text-muted-foreground'
                            } ${active ? 'ring-4 ring-primary/20' : ''}`}>
                              <Icon size={16} strokeWidth={2} />
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${reached ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {order.order_status === 'shipped' && order.tracking_awb && (
                    <div className="mt-6 p-4 border border-cyan-500/30 bg-cyan-500/5 rounded">
                      <div className="flex items-center gap-3 flex-wrap">
                        <Truck size={20} className="text-cyan-400" strokeWidth={1.5} />
                        <div className="flex-1">
                          <p className="text-xs uppercase tracking-widest text-muted-foreground">Tracking / AWB</p>
                          <p className="font-mono text-cyan-300">{order.tracking_awb}</p>
                        </div>
                        {order.tracking_url && (
                          <a href={order.tracking_url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm flex items-center gap-1">
                            Track on Courier <ExternalLink size={12} strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border border-red-500/30 bg-red-500/5 p-6 flex items-start gap-3">
                  <XCircle className="text-red-400 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <div>
                    <p className="font-medium text-foreground">
                      Order {order.order_status === 'refunded' ? 'Refunded' : 'Cancelled'}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">{STATUS_COPY[order.order_status]}</p>
                  </div>
                </div>
              )}

              {/* Items */}
              <div className="border border-white/10 p-6 md:p-8">
                <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><Receipt size={20} /> Order Summary</h3>
                <div className="space-y-3">
                  {(order.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between items-start border-b border-white/5 pb-3">
                      <div>
                        <p className="font-medium">{item.name || item.product_id}</p>
                        <p className="text-xs text-muted-foreground">Qty {item.quantity} × {fmtINR(item.price)}</p>
                      </div>
                      <p>{fmtINR((item.price || 0) * (item.quantity || 0))}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2 text-sm">
                  {order.discount_amount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ''}</span>
                      <span>− {fmtINR(order.discount_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Shipping ({order.shipping_method || 'Standard'})</span>
                    <span>{fmtINR(order.shipping_cost || 0)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-white/10 text-lg font-medium">
                    <span>Total</span>
                    <span className="text-primary font-accent italic text-2xl">{fmtINR(order.total_amount)}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 italic">
                  All prices are inclusive of GST. A physical invoice will be shared separately on WhatsApp / email.
                </p>
              </div>

              {/* Shipping Address */}
              <div className="border border-white/10 p-6 md:p-8">
                <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><MapPin size={20} /> Shipping Address</h3>
                <p>{order.customer_name}</p>
                <p className="text-muted-foreground">
                  {order.shipping_address}
                  {order.shipping_city && <>, {order.shipping_city}</>}
                  {order.shipping_state && <>, {order.shipping_state}</>}
                  {order.shipping_pincode && <> — {order.shipping_pincode}</>}
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};
